import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../../app/store";
import configUrls from "../../../config/configUrls";
import { DocumentoAssinarList } from "../../../types/documento/assinar/DocumentoAssinarList";
import { useGetDocumentoAssinaturasQuery } from "./documentoAssinarApiSlice";

// Hook personalizado para gerenciar a busca
const useSearch = () => {
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]);

  return {
    search,
    debouncedSearch,
    handleChangeSearch,
    inputRef,
    cursorPosition,
  };
};

// Componente para renderizar as ações da tabela
const TableActions = ({
  rowData,
  idDocumento,
}: {
  rowData: DocumentoAssinarList;
  idDocumento: string;
}) => (
  <Box>
    <Tooltip title="Visualizar">
      <Link
        to={`${configUrls.DOCUMENTO_VERSAO_VIEW_LINK}${idDocumento}/${rowData.id}`}
        //  link: `${configUrls.PROCESSO_DOCUMENTO_VIEW_LK}${doc.id}`
      >
        <IconButton color="secondary" aria-label="edit">
          <PageviewOutlinedIcon />
        </IconButton>
      </Link>
    </Tooltip>
  </Box>
);

export const DocumentoAssinarListForm = () => {
  const idDocumento = useParams().id || "";

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { processoSelecionado } = useSelector(
    (state: RootState) => state.processoSelecionado
  );
  const [loading, setLoading] = useState(false);
  const [versoes, setAssinaturas] = useState<DocumentoAssinarList[]>([]);

  const {
    search,
    debouncedSearch,
    handleChangeSearch,
    inputRef,
    cursorPosition,
  } = useSearch();

  const { data, isFetching, error } = useGetDocumentoAssinaturasQuery({
    id: idDocumento ?? "",
    search: debouncedSearch,
  });

  useEffect(() => {
    if (!processoSelecionado) {
      alert("Nenhum processo selecionado");
      window.location.href = configUrls.PROCESSO_LIST_RT;
    }
  }, [processoSelecionado]);

  useEffect(() => {
    if (data) {
      setAssinaturas(data);
    }
    if (error) {
      enqueueSnackbar("Assinaturas não encontradas!", { variant: "error" });
    } else if (isFetching) {
      enqueueSnackbar("Buscando registros...", { variant: "info" });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(
        cursorPosition.current,
        cursorPosition.current
      );
    }
  }, [data, error, isFetching, enqueueSnackbar, inputRef, cursorPosition]);

  if (isFetching || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const renderData = (rowData: DocumentoAssinarList) => (
    <Typography color="secondary" variant="body2">
      {rowData.dataHoraCriacao
        ? format(rowData.dataHoraCriacao, "dd/MM/yyyy HH:mm:ss")
        : "Data não disponível"}
    </Typography>
  );

  const renderUsuario = (rowData: DocumentoAssinarList) => (
    <Typography color="secondary" variant="body2">
      {rowData.usuario?.nome || "Usuário não disponível"}
    </Typography>
  );

  return (
    <>
      <Paper sx={{ width: "100%", height: "100vh", padding: "12px" }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography color="secondary" variant="h6" gutterBottom>
                  Assinaturas para o documento:{" "}
                  {processoSelecionado?.numeroProcesso}
                </Typography>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    //disabled={true}
                    onClick={() => {
                      navigate(
                        `${configUrls.DOCUMENTO_ASSINAR_LINK}${idDocumento}`
                      );
                    }}
                  >
                    Assinar
                  </Button>

                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate(-1)}
                  >
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField
                label="Digite para pesquisar"
                name="nome"
                onChange={handleChangeSearch}
                size="small"
                inputProps={{ maxLength: 100 }}
                value={search}
                inputRef={inputRef}
              />
            </FormControl>
          </Grid>
        </Grid>

        <DataTable
          value={versoes}
          paginator
          rows={25}
          showGridlines={true}
          size="small"
          columnResizeMode="expand"
          resizableColumns
          dataKey="id"
          header={
            <Typography variant="body2" color="textSecondary">
              Assinatura(s) encontrada(s): {versoes.length}
            </Typography>
          }
          className="custom-datatable"
        >
          <Column
            field="acoes"
            header="Ver"
            body={(rowData) => (
              <TableActions rowData={rowData} idDocumento={idDocumento} />
            )}
            style={{ width: "20%" }}
          />
          <Column
            header="Data"
            body={renderData}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            header="Usuário"
            body={renderUsuario}
            sortable
            style={{ width: "auto" }}
          />
        </DataTable>
      </Paper>
    </>
  );
};

//fazer a rotina de visualizar a assinar
